import React, { useState, useEffect } from "react";
import {
  History,
  ArrowLeft,
  Clock,
  Calendar,
  MessageSquare,
  Paperclip,
  Bot,
  User,
  FileText,
  File,
  FileCode,
  FileSpreadsheet,
  FileImage,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import MainHeader from "../MainHeader";
import { useAuth } from "../hooks/useAuth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
// 파일 타입에 따른 아이콘 반환
const getFileIcon = (fileName) => {
  const extension = fileName.split(".").pop().toLowerCase();
  switch (extension) {
    case "pdf":
      return <FileText className="w-4 h-4" />;
    case "doc":
    case "docx":
      return <File className="w-4 h-4" />;
    case "xls":
    case "xlsx":
    case "csv":
      return <FileSpreadsheet className="w-4 h-4" />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return <FileImage className="w-4 h-4" />;
    case "js":
    case "jsx":
    case "ts":
    case "tsx":
    case "json":
    case "xml":
      return <FileCode className="w-4 h-4" />;
    default:
      return <File className="w-4 h-4" />;
  }
};

// 파일 타입에 따른 색상 반환
const getFileColor = (fileName) => {
  const extension = fileName.split(".").pop().toLowerCase();
  switch (extension) {
    case "pdf":
      return "from-red-400 to-red-500";
    case "doc":
    case "docx":
      return "from-blue-400 to-blue-500";
    case "xls":
    case "xlsx":
    case "csv":
      return "from-green-400 to-green-500";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "from-purple-400 to-purple-500";
    case "js":
    case "jsx":
    case "ts":
    case "tsx":
    case "json":
    case "xml":
      return "from-yellow-400 to-orange-500";
    default:
      return "from-gray-400 to-gray-500";
  }
};

export default function DeepResearchHistory() {
  const { api } = useAuth(); // baseURL already includes "/api/v1"
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  // 히스토리 목록 불러오기
  const fetchHistory = async (page) => {
    try {
      const response = await api.get("/deep_research/history", {
        params: { p: page },
      });
      const data = response.data.data || [];
      console.log(data);
      setChatHistory(data);
      // 현재 선택된 히스토리가 사라졌다면 초기화
      if (selectedHistory && !data.find((h) => h.id === selectedHistory.id)) {
        setSelectedHistory(null);
        setMessages([]);
      }
    } catch (err) {
      console.error("히스토리 조회 중 오류:", err);
    }
  };

  useEffect(() => {
    fetchHistory(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // 선택된 히스토리 변경 시 해당 세션의 대화 메시지 불러오기
  useEffect(() => {
    if (!selectedHistory) {
      setMessages([]);
      return;
    }
    const fetchMessages = async () => {
      try {
        const response = await api.get("/deep_research/history/view", {
          params: { session_id: selectedHistory.id },
        });
        const data = response.data.data || [];
        const mapped = data.map((m) => {
          let ts = "";
          try {
            ts = new Date(m.created_at).toLocaleTimeString("ko-KR", {
              hour: "2-digit",
              minute: "2-digit",
            });
          } catch {
            ts = "";
          }
          return {
            id: m.id,
            type: m.role === "user" ? "user" : "assistant",
            content: m.chat_message || "",
            timestamp: ts,
          };
        });
        setMessages(mapped);
      } catch (err) {
        console.error("메시지 조회 중 오류:", err);
      }
    };
    fetchMessages();
  }, [api, selectedHistory]);

  // 삭제 처리
  const removeHistory = async (id) => {
    try {
      await api.post("/deep_research/history/remove", { id });
      // 현재 페이지 다시 로드
      fetchHistory(currentPage);
    } catch (err) {
      console.error("삭제 중 오류:", err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 날짜 포맷 함수 (iso 문자열 → "YYYY-MM-DD")
  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("ko-KR");
    } catch {
      return "";
    }
  };

  // Prev/Next 버튼 활성 여부
  const hasPrev = currentPage > 1;
  const hasNext = chatHistory.length === itemsPerPage;

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 relative overflow-hidden">
        {/* 배경 효과 */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-400/30 rounded-full filter blur-[100px] animate-pulse" />
          <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-purple-400/30 rounded-full filter blur-[120px] animate-pulse delay-700" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-400/20 rounded-full filter blur-[100px] animate-pulse delay-1000" />
          <div
            className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"
            opacity-20
          />
        </div>

        <div className="relative z-10">
          {/* 헤더 */}
          <div className="px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <button
                onClick={() => window.history.back()}
                className="group flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                돌아가기
              </button>

              <div className="flex items-center gap-4 mb-8">
                <div className="relative">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl flex items-center justify-center">
                    <History className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl blur opacity-50" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">
                    분석 히스토리
                  </h1>
                  <p className="text-blue-100 mt-1">
                    이전 문서 분석 내용을 확인하세요
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 컨텐츠 영역 */}
          <div className="w-full px-6 pb-12">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* 히스토리 목록 */}
              <div className="lg:col-span-1 space-y-4">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    최근 분석
                  </h3>
                  <div className="space-y-3">
                    {chatHistory.map((chat) => (
                      <div
                        key={chat.id}
                        className={`relative p-4 rounded-xl cursor-pointer transition-all ${
                          selectedHistory?.id === chat.id
                            ? "bg-white/20 shadow-lg scale-[1.02]"
                            : "bg-white/10 hover:bg-white/15"
                        }`}
                      >
                        <div
                          onClick={() => setSelectedHistory(chat)}
                          className="pr-8"
                        >
                          <h4 className="font-medium text-white mb-2">
                            {chat.subject}
                          </h4>
                          <div className="flex items-center gap-3 text-sm text-blue-100 mb-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(chat.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {chat.messages}
                            </span>
                          </div>

                          {Array.isArray(chat.documents) &&
                            chat.documents.length > 0 && (
                              <div className="flex items-center gap-2">
                                <div className="flex -space-x-2">
                                  {chat.documents
                                    .slice(0, 3)
                                    .map((doc, idx) => (
                                      <div
                                        key={idx}
                                        className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getFileColor(
                                          doc.name
                                        )} flex items-center justify-center text-white shadow-md`}
                                        style={{ zIndex: 3 - idx }}
                                      >
                                        {getFileIcon(doc.name)}
                                      </div>
                                    ))}
                                </div>
                                <span className="text-xs text-blue-200">
                                  {chat.documents.length}개 문서
                                  {chat.documents.length > 3 &&
                                    ` (+${chat.documents.length - 3})`}
                                </span>
                              </div>
                            )}
                        </div>
                        <button
                          onClick={() => removeHistory(chat.id)}
                          className="absolute top-3 right-3 text-red-400 hover:text-red-300"
                          title="삭제"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Prev / Next 버튼 */}
                  <div className="mt-6 flex items-center justify-center gap-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={!hasPrev}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        !hasPrev
                          ? "bg-white/5 text-white/30 cursor-not-allowed"
                          : "bg-white/10 text-white hover:bg-white/20"
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-white/70">{currentPage}</span>
                    <button
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                      disabled={!hasNext}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        !hasNext
                          ? "bg-white/5 text-white/30 cursor-not-allowed"
                          : "bg-white/10 text-white hover:bg-white/20"
                      }`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* 대화 내용 + 문서 패널 */}
              <div className="lg:col-span-2">
                {selectedHistory ? (
                  <div className="flex gap-4">
                    {/* 채팅 영역 */}
                    <div className="flex-1 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
                      <div className="bg-white/10 p-6 border-b border-white/20 flex justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-2">
                            {selectedHistory.subject}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-blue-100">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(selectedHistory.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Paperclip className="w-4 h-4" />
                              {(selectedHistory.documents || []).length}개 파일
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="p-6 max-h-[600px] overflow-y-auto">
                        <div className="space-y-6">
                          {messages.length === 0 ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <Bot className="w-16 h-16 text-white/20 mx-auto mb-4" />
                                <p className="text-white/60">
                                  이전 대화가 없습니다
                                </p>
                              </div>
                            </div>
                          ) : (
                            messages.map((message) => (
                              <div
                                key={message.id}
                                className={`flex gap-3 ${
                                  message.type === "user"
                                    ? "justify-end"
                                    : "justify-start"
                                }`}
                              >
                                <div
                                  className={`max-w-[80%] ${
                                    message.type === "user" ? "text-right" : ""
                                  }`}
                                >
                                  <div
                                    className={`inline-block p-2 rounded-2xl ${
                                      message.type === "user"
                                        ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white"
                                        : "bg-white/10 backdrop-blur text-white"
                                    }`}
                                  >
                                    <p className="whitespace-pre-wrap">
                                      <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                      >
                                        {message.content}
                                      </ReactMarkdown>
                                    </p>
                                  </div>
                                  <p className="text-xs text-blue-200 mt-1 px-2">
                                    {message.timestamp}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 참조 문서 사이드바 */}
                    <div className="w-80">
                      <div className="w-80 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 h-full">
                        <div className="p-4 border-b border-white/20">
                          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Paperclip className="w-5 h-5" />
                            참조 문서
                          </h4>
                          <p className="text-xs text-blue-200 mt-1">
                            {(selectedHistory.documents || []).length}개 파일
                          </p>
                        </div>

                        <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
                          {(selectedHistory.documents || []).map((doc, idx) => (
                            <div
                              key={idx}
                              className="group bg-white/10 rounded-xl p-4 hover:bg-white/15 transition-all cursor-pointer border border-white/10 hover:border-white/20"
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getFileColor(
                                    doc.name
                                  )} flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}
                                >
                                  {getFileIcon(doc.name)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-white font-medium truncate mb-1">
                                    {doc.name}
                                  </p>
                                  <p className="text-xs text-blue-200">
                                    {doc.size}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-16 text-center">
                    <div className="relative inline-block">
                      <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <History className="w-12 h-12 text-white/60" />
                      </div>
                      <div className="absolute -inset-2 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-3xl blur-xl" />
                    </div>
                    <p className="text-xl text-white/80">
                      대화를 선택하여 내용을 확인하세요
                    </p>
                    <p className="text-blue-200 mt-2">
                      이전에 분석한 문서와 대화 내용이 여기에 표시됩니다
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
